import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {BokkzIbDataService, IncomeTaxEntity, ReiskostenZorgGezinslid} from '@snelstart-ventures/fiscaal/domain-logic';
import {FormArray, FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  shareReplay,
  Subscription,
  switchMap,
  take,
  tap
} from 'rxjs';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

type ReiskostenZorgGezinslidFormGroup = FormGroup<{
    omschrijving: FormControl<string | null>;
    aantalBezoeken: FormControl<number | null>;
    afstandRetour: FormControl<number | null>;
    werkelijkeKostenOV: FormControl<number | null>;
}>;

@Component({
    selector: 'snelstart-venture-fiscaal-feature-reiskosten-zorg-gezinslid',
    templateUrl: './reiskosten-zorg-gezinslid.component.html',
    styleUrls: ['./reiskosten-zorg-gezinslid.component.scss'],
    imports: [SharedUiButtonModule, ReactiveFormsModule, SharedUiDetailsModule, SharedUiFormControlsModule, CommonModule],
})
export class ReiskostenZorgGezinslidComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
    private dataService = inject(BokkzIbDataService);
    private destroyRef = inject(DestroyRef);
    private changeSubscriptions = new Subscription();

    public formGroup = new FormGroup({
        reiskostenZorgGezinslid: new FormArray<ReiskostenZorgGezinslidFormGroup>([]),
    });

    readonly incomeTaxEntity$: Observable<IncomeTaxEntity> = this.dataService.incomeTaxEntity$.pipe(
        filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
        takeUntilDestroyed(this.destroyRef),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    public ngOnInit(): void {
        this.incomeTaxEntity$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
            this.updateForm(data.aangifte?.persoonsgebondenAftrek?.pgaSpecifiekeZorgkosten?.reiskostenZorgGezinslid || []);
        });
        this.subscribeToChanges();
    }

    ngOnDestroy(): void {
        this.changeSubscriptions.unsubscribe();
    }

    private subscribeToChanges(): void {
        this.changeSubscriptions.unsubscribe();
        this.changeSubscriptions = new Subscription();

        const extractRelevant = () =>
            this.reiskostenZorgGezinslid.getRawValue().map((w) => ({
                omschrijving: w.omschrijving,
                aantalBezoeken: w.aantalBezoeken,
                afstandRetour: w.afstandRetour,
                werkelijkeKostenOV: w.werkelijkeKostenOV,
            }) as ReiskostenZorgGezinslid);

        this.changeSubscriptions.add(
            this.reiskostenZorgGezinslid.valueChanges
                .pipe(
                    debounceTime(500),
                    tap(() => this.saveForm()),
                    map(() => extractRelevant()),
                    distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
                    switchMap(() => this.dataService.calculateLatestIncomeTaxReturn()),
                    takeUntilDestroyed(this.destroyRef),
                )
                .subscribe(),
        );
    }

    get reiskostenZorgGezinslid(): FormArray {
        return this.formGroup.controls.reiskostenZorgGezinslid;
    }

    addRij(): void {
        this.reiskostenZorgGezinslid.push(this.createFormGroup());
    }

    verwijderRij(index: number): void {
        this.reiskostenZorgGezinslid.removeAt(index, { emitEvent: false });
        this.formGroup.updateValueAndValidity({ emitEvent: false });
        this.formGroup.markAsDirty({ emitEvent: false });
        this.saveForm();
    }

    createFormGroup(kosten?: ReiskostenZorgGezinslid): FormGroup {
        return new FormGroup({
            omschrijving: new FormControl<string | null>(kosten?.omschrijving ?? null),
            aantalBezoeken: new FormControl<number | null>(kosten?.aantalBezoeken ?? null),
            afstandRetour: new FormControl<number | null>(kosten?.afstandRetour ?? null),
            werkelijkeKostenOV: new FormControl<number | null>(kosten?.werkelijkeKostenOV ?? null),
        }) as ReiskostenZorgGezinslidFormGroup;
    }

    private updateForm(data: ReiskostenZorgGezinslid[]): void {
        const lijst = data || [];
        const formArray = this.reiskostenZorgGezinslid;

        while (formArray.length > lijst.length) {
            formArray.removeAt(formArray.length - 1, { emitEvent: false });
        }

        for (let i = 0; i < lijst.length; i++) {
            if (i < formArray.length) {
                const control = formArray.at(i) as FormGroup;
                control.patchValue(lijst[i], { emitEvent: false });
            } else {
                const formGroup = this.createFormGroup(lijst[i]);
                formArray.push(formGroup, { emitEvent: false });
            }
        }
    }

    saveForm(): void {
        const reiskostenZorgGezinslid = this.formGroup
            .getRawValue()
            .reiskostenZorgGezinslid.filter(
                (restant) => restant.omschrijving || restant.aantalBezoeken || restant.afstandRetour || restant.werkelijkeKostenOV,
            )
            .map(
                (verplichtingen) =>
                    ({
                        omschrijving: verplichtingen.omschrijving,
                        aantalBezoeken: verplichtingen.aantalBezoeken,
                        afstandRetour: verplichtingen.afstandRetour,
                        werkelijkeKostenOV: verplichtingen.werkelijkeKostenOV,
                    }) as ReiskostenZorgGezinslid,
            );

        this.dataService.patchIncomeTaxEntity((currentEntity) => {
            if (!currentEntity.aangifte) {
                return currentEntity;
            }

            return {
                ...currentEntity,
                aangifte: {
                    ...currentEntity.aangifte,
                    persoonsgebondenAftrek: {
                        ...currentEntity.aangifte.persoonsgebondenAftrek,
                        pgaSpecifiekeZorgkosten: {
                            ...currentEntity.aangifte.persoonsgebondenAftrek?.pgaSpecifiekeZorgkosten,
                            reiskostenZorgGezinslid: reiskostenZorgGezinslid,
                        },
                    },
                },
            } as IncomeTaxEntity;
        });
    }
}
